/* eslint-disable @typescript-eslint/naming-convention */
import React, { FC } from 'react'
import ReactDOM from 'react-dom'

interface ILayerProps<TResult = void> {
  /** 确认 */
  resolve(result: TResult | Promise<TResult> | PromiseLike<TResult>): void;
  /** 取消 */
  reject(reason?: any): void;
  close(key: string): void;
  /** 关闭所有弹窗 */
  closeAll(): void;
}

interface IModalItem {
  resolve: (any?) => any;
  reject: (any?) => any;
  key: string;
  wrapper: HTMLElement;
  id: number;
}

const stack: IModalItem[] = []
/** 弹窗唯一id */
let uid = 1

function keyRemove (item: IModalItem) {
  if (document.querySelector('body #use-layer-wrapper-' + item.id)) {
    item.wrapper.remove()
  } else {
    setTimeout(() => keyRemove(item), 50)
  }
}

function destroy (item: IModalItem) {
  setTimeout(() => {
    stack.splice(stack.findIndex((_item: any) => _item.id === item.id), 1)
    ReactDOM.unmountComponentAtNode(item.wrapper)
    item.wrapper.remove()
  }, 0)
}

const create = (key: string) => {
  let wrapper = document.getElementById(key)
  if (wrapper === null) {
    wrapper = document.body.appendChild(document.createElement('div'))
    wrapper.id = key
  }
  return wrapper
}

/** TProps:是组件的IProps，TResult是resolve结果的类型，TResult可选默认void */
function UseLayer<TProps, TResult = void>(Component: FC<TProps>, options?: TProps, key?: string, isPreventRepeat?: boolean): Promise<TResult>
function UseLayer<TProps extends ILayerProps<TResult>, TResult> (Component: FC<TProps>, options: Omit<TProps, 'resolve' | 'reject'> = {} as TProps, inkey?: string, isPreventRepeat?: boolean): Promise<TResult> {
  const key = inkey || (Component as any)?.type?.displayName || Date.now().toString()
  // 一个key相同的弹窗不会重复弹起会先reject之前的弹窗然后再激活新的弹窗
  if (key) {
    const keyUseLayer = stack.find((s: any) => s.key === key)
    if (keyUseLayer) {
      // TODO 修改一种机制来实现
      if (isPreventRepeat) {
        return Promise.reject()
      }
      // 如果已经有key相同的弹窗，拦截弹窗挂载，且执行resolve方法
      return Promise.resolve().then()
    }
  }
  // key = key || Date.now().toString()
  const wrapper = create(`use-layer-wrapper-${uid++}`)
  const item: IModalItem = {} as any
  const result = new Promise<TResult>((resolve, reject) => {
    item.resolve = resolve
    item.reject = reject
    item.key = key
    item.wrapper = wrapper
    item.id = uid
    const props = { ...options, resolve, reject } as TProps
    ReactDOM.render(
      <Component
        {...props}
      />, wrapper)
  }).finally(() => {
    destroy(item)
  })
  stack.push(item)
  return result
}

UseLayer.closeAll = () => {
  stack.forEach((item: any) => {
    item.reject()
  })
  document.querySelectorAll('ion-modal').forEach(dom => {
    dom.remove()
  })
}

UseLayer.closeAllExcept = (key: string) => stack.forEach((item: any) => {
  if (item.key !== key) {
    item.reject()
  }
})

UseLayer.close = (key: string) => {
  if (stack.length) {
    const item = key ? stack.find((item: any) => item.key === key) : stack[stack.length - 1]
    if (item) {
      item.reject()
    }
  }
}

UseLayer.resolve = (data?: any) => {
  if (stack.length) {
    stack[stack.length - 1].resolve(data)
  }
}

UseLayer.reject = (data?: any) => {
  if (stack.length) {
    stack[stack.length - 1].reject(data)
  }
}

UseLayer.has = (key: string) => {
  if (stack.length) {
    return !!stack.find(st => st.key === key)
  }
  return false
}

UseLayer.get = (key?: string) => key ? stack.find((item: any) => item.key === key) : stack

export default UseLayer
